import { IconButton } from '@salecycle/ui-component-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useWorkflowForm } from '../../contexts/CampaignWorkflowFormContext';
import { ActionMenuItem } from '@salecycle/ui-component-library/ActionsMenuItem/ActionsMenuItem';
import { ActionsMenu } from '@salecycle/ui-component-library/ActionsMenu/ActionMenu';
import { DrawerType } from '../../state/Drawer/DrawerState';
import { Variant } from '../../state/SplitTests/SplitTestsState';
import { useTranslation } from 'react-i18next';

interface ButtonOnlyProps extends IntrinsicProps {
  actionsMenuAriaLabel?: string;
  buttonOnly: boolean;
  onClick: () => void;
}

interface ActionsProps extends IntrinsicProps {
  actionsMenuAriaLabel?: string;
  onRemoveAriaLable?: string;
  variants?: Variant[];
  onEditVariant?: (id?: string, type?: DrawerType) => void;
  onDelete?: () => void;
  onEdit?: (id?: string, type?: DrawerType) => void;
  onEnableSplitTest?: () => void;
}

type WorkflowActionsMenuProps = ButtonOnlyProps | ActionsProps;

const ActionMenuContainer = styled.div`
  position: absolute;
  margin-top: 4px;
`;

const EditArea = styled.div<{ isButtonOnly: boolean }>`
  position: relative;
  z-index: ${props => (props.isButtonOnly ? '0' : '9')};
`;

export function WorkflowActionsMenu(props: WorkflowActionsMenuProps) {
  const { t } = useTranslation('campaigns');

  const [isMenuOpen, setOpenMenu] = useState(false);
  const { formDisabled } = useWorkflowForm();
  const menuRef = useRef(null);

  const onOpenActionMenu = useCallback(() => {
    setOpenMenu(!isMenuOpen);
  }, [isMenuOpen]);

  const handleOutsideClicks = event => {
    if (isMenuOpen && menuRef?.current && !menuRef?.current?.contains(event.target)) {
      setOpenMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClicks);

    return () => {
      document.removeEventListener('click', handleOutsideClicks);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [isMenuOpen]);

  return (
    <EditArea isButtonOnly={'buttonOnly' in props}>
      {!('buttonOnly' in props) ? (
        <IconButton
          svgIcon
          ariaLabel={props.actionsMenuAriaLabel}
          icon="ellipsis-vertical"
          onClick={onOpenActionMenu}
          isActive={isMenuOpen ? true : false}
          disabled={formDisabled}
        />
      ) : (
        <IconButton
          svgIcon
          ariaLabel={props.actionsMenuAriaLabel}
          icon="pen"
          disabled={formDisabled}
          onClick={() => props.onClick()}
        />
      )}
      {isMenuOpen && !('buttonOnly' in props) && (
        <ActionMenuContainer ref={menuRef}>
          <ActionsMenu>
            <ActionMenuItem
              aria-label="edit"
              icon="pen"
              onClick={() => {
                setOpenMenu(false);
                props.onEdit();
              }}
            >
              {t('onsite.editor.workflow.actionMenu.edit')}
            </ActionMenuItem>
            {!!props.variants &&
              props?.variants.map((item, key) => (
                <ActionMenuItem
                  key={key}
                  aria-label="actionMenuItem"
                  icon="pen"
                  onClick={() => {
                    props.onEditVariant(item.messageId, 'display-web-content-variant-settings');
                  }}
                >
                  {t('onsite.editor.workflow.actionMenu.editVariant', {
                    variantName: item.name,
                  })}
                </ActionMenuItem>
              ))}
            {!!props.onEnableSplitTest && (
              <ActionMenuItem
                aria-label="split-test"
                icon="vials"
                onClick={() => {
                  setOpenMenu(false);
                  props.onEnableSplitTest();
                }}
              >
                {t('onsite.editor.workflow.actionMenu.createSplitTest')}
              </ActionMenuItem>
            )}
            {!!props.onDelete && (
              <ActionMenuItem aria-label={'delete'} icon="trash" onClick={() => props.onDelete()}>
                {t('onsite.editor.workflow.actionMenu.delete')}
              </ActionMenuItem>
            )}
          </ActionsMenu>
        </ActionMenuContainer>
      )}
    </EditArea>
  );
}
